import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http'; 

import { MatDialog } from '@angular/material/dialog';
import { MessageDialogComponent } from '../../../../shared/dialogs/components/message-dialog.component';

import { BehaviorSubject, Observable, Subject, forkJoin, of } from 'rxjs';
import { map, switchMap, withLatestFrom } from 'rxjs/operators';

import { ActivePossibilities } from '../../../../shared/suggestable-input/entities/active-possibilities.entity';
import { CategoryBuilder } from '../../../../shared/suggestable-input/components/suggestable-input.component';
import { SubjectRelay } from '../../../../shared/commands/subject-relay.class';
import { EntrepriseService } from '../../../entreprises/services/entreprise.service';
import { EntrepriseTLR } from '../../../entreprises/entities/entreprise-tlr.entity';
import { IndicatifTelephone } from '../../../../static-types/entities/indicatif-telephone.entity';
import { StaticTypeService } from '../../../../static-types/services/static-types.service';
import { UserTLR } from '../../user/entities/user-tlr.entity';

import { IntervenantTLR } from '../entities/intervenant-tlr.entity';
import { IntervenantService } from '../services/intervenant.service';
import { SuggestionNomIntervenant } from '../entities/suggestion-nom-intervenant.entity';

enum Mode {
	CREATION,
	UPDATE
}

type IndicatifMap = {
	[dialCode: string]: IndicatifTelephone
}

@Component({
	selector: 'edit-intervenant',
	templateUrl: 'edit-intervenant.html',
	styleUrls: ['edit-intervenant.css']
})
export class EditIntervenantComponent {
	// Element de comportement de la fenetre
	popupVisibility$ = new Subject<boolean>();
	modes = Mode;
	mode!: Mode;
	title!: string;
	private internEmailPattern = /^[A-Za-z0-9]{6,6}@engie\.com$/;
	getIndicatifLabel = (it: IndicatifTelephone) => it ? (it.name + ' (' + it.code + ') ' + it.dialCode) : '';
	indicatifTelephone$ = new BehaviorSubject<IndicatifTelephone | null>(null);
	telephone$ = new BehaviorSubject<number | null>(null);
	getEntrepriseNom = (entreprise: EntrepriseTLR | null) => entreprise ? entreprise.nom : '';
	entreprise$ = new BehaviorSubject<EntrepriseTLR | null>(null);
	waitingForServer: boolean = false;

	// Elements de controle du comportement des suggestions de noms d'intervenant
	observablePourSuggestionNom = (criteria: string) => this.intervenantService.getSuggestionsNomsIntervenants(criteria, 15);
	suggestionCategoryBuilders: CategoryBuilder<ActivePossibilities, SuggestionNomIntervenant>[] = 	[
		{
			titleGetter: (_possibles: ActivePossibilities) => "Noms similaires:",
			suggestionsFilter: (_suggestion: SuggestionNomIntervenant) => false,
		},
		{
			titleGetter: (possibles: ActivePossibilities) => "Actifs (" + possibles.actifs + ")",
			suggestionsFilter: (suggestion: SuggestionNomIntervenant) => suggestion.obsolete === 0,
		},
		{
			titleGetter: (possibles: ActivePossibilities) => "Inactifs (" + possibles.inactifs + ")",
			suggestionsFilter: (suggestion: SuggestionNomIntervenant) => suggestion.obsolete === 1,
		},
	];
	getSuggestionsLabel = (suggestion: SuggestionNomIntervenant) => '<strong>' + suggestion.nom + '</strong>&nbsp;' + suggestion.prenom + '&nbsp;<em>(' + suggestion.nomEntreprise + ')</em>';
	getSuggestionsLabelForInput = (suggestion: SuggestionNomIntervenant) => suggestion.nom;

	// Donnees de fond
	indicatifTelephoneOptions: IndicatifTelephone[] = [];
	availableEntreprises: EntrepriseTLR[] = [];

	// Donnees chargees
	user!: UserTLR;
	intervenant!: IntervenantTLR;
	obsoleteCurrentEntreprise: EntrepriseTLR | null = null;

	constructor( //
		private dialog: MatDialog,

		@Inject(EntrepriseService) entrepriseService: EntrepriseService,
		@Inject(IntervenantService) private intervenantService: IntervenantService,
		@Inject(StaticTypeService) staticTypeService: StaticTypeService,
	) {
		// Chargement des indicatifs de telephone
		let indicatifsTelephone$ = staticTypeService.getIndicatifTelephoneList();
		indicatifsTelephone$.subscribe(
			(indicatifs) => this.indicatifTelephoneOptions = indicatifs
		)
		
		let indicatifsByDialCode$ = indicatifsTelephone$.pipe(
			map(
				(indicatifs) => {
					let map: IndicatifMap = {};
					indicatifs.forEach(
						(indicatif) => map[indicatif.dialCode] = indicatif
					)
					return map;
				}	
			)
		);
		
		// Quand l'indicatif de telephone change, mettre a jour l'intervenant
		this.indicatifTelephone$.subscribe(
			(indicatif) => {
				if (!this.intervenant) {
					return;
				}
				if (indicatif) {
					this.intervenant.indicatifTelephone = indicatif.dialCode
				}
			}
		);
		
		// Quand le numero de telephone change, mettre a jour l'intervenant
		this.telephone$.subscribe(
			(numero) => {
				if (!this.intervenant) {
					return;
				}
				if (numero === null) {
					this.intervenant.telephone = null;
				} else {
					this.intervenant.telephone = "" + numero;
				}
			}
		)
		
		// Quand l'entreprise change, mettre a jour l'internvenant
		this.entreprise$.subscribe(
			(entreprise) => {
				if (!this.intervenant) {
					return;
				}
				if (this.obsoleteCurrentEntreprise && entreprise === null) {
					// Cas special: l'entreprise actuelle est obsolete, et l'utilisateur n'a selectionne aucune nouvelle entrperise
					// Restaurer l'id de l'ancienne entre priser dans l'intervenant
					this.intervenant.entrepriseId = this.obsoleteCurrentEntreprise.id;
					return;
				}
				
				this.intervenant.entrepriseId = entreprise ? entreprise.id : null;
			}
		);
		
		
		// Evaluer les entreprises disponibles en selection
		let availableEntreprises$ = entrepriseService.getActiveEntreprisesTLRList();
		availableEntreprises$.subscribe(
			(entreprises) => {
				this.availableEntreprises = entreprises;
			}
		)
		
		let completedOpenRequest$ = this.openRequest$.pipe(
			withLatestFrom(indicatifsByDialCode$, availableEntreprises$),
			switchMap(
				([intervenant, indicatifsByDialCode, availableEntreprises]) => {
					let entreprise$: Observable<EntrepriseTLR | null>;
					let unavailableEntreprise$: Observable<EntrepriseTLR | null>;
					 
					if (intervenant === null || intervenant.entrepriseId === null) {
						entreprise$ = of(null);
						unavailableEntreprise$ = of(null);
					} else {
						// Dans le cas d'une modification d'intervenant existant, controler si l'entreprise
						// fait partie des entreprises disponibles dans la liste
						let entrepriseId = intervenant.entrepriseId;
						let index = availableEntreprises.findIndex($ => $.id === entrepriseId);
						if (index >= 0) {
							entreprise$ = of(availableEntreprises[index]);
							unavailableEntreprise$ = of(null);
						} else {
							entreprise$ = of(null);
							unavailableEntreprise$ = entrepriseService.getEntrepriseTLRById(entrepriseId);
						}
					}
					
					
					// Si l'entreprise n'est pas disponible, il faut la charger independamment
					return forkJoin([
						of(intervenant),
						of(indicatifsByDialCode),
						entreprise$,
						unavailableEntreprise$,
					]);
				}
			)
		);
		
		completedOpenRequest$.subscribe(
			([intervenant, indicatifsByDialCode, entreprise, unavailableEntreprise]) => {
				if (intervenant === null) {
					this.mode = Mode.CREATION;

					// Definition du titre de la pop-up
					this.title = 'Création d\'un intervenant';

					// Memorisation du IntervenantTLR dans son etat d'avant la saisie utilisateur
					this.intervenant = new IntervenantTLR();
					
					this.indicatifTelephone$.next(null);
					this.telephone$.next(null);
				} else {
					this.mode = Mode.UPDATE;

					// Definition du titre de la pop-up
					this.title = 'Modification de l\'intervenant ' + intervenant.nom;

					// Creation d'une copie de l'intervenant pour permettre la modification de celui-ci
					// sans alterer l'objet d'origine
					this.intervenant = { ...intervenant } as IntervenantTLR;
					
					let dialCode = intervenant.indicatifTelephone;
					let indicatif = dialCode == null ? null : indicatifsByDialCode[dialCode];
					this.indicatifTelephone$.next(indicatif);
					let numero = intervenant.telephone === null ? null : +intervenant.telephone;
					this.telephone$.next(numero);
				}

				this.entreprise$.next(entreprise);
				this.obsoleteCurrentEntreprise = unavailableEntreprise;

				// Affichage de la pop-up
				this.popupVisibility$.next(true);
			}
		)
	}

	// Liaison de l'observable alertante des changements de filtres
	private openRequest$ = new SubjectRelay<IntervenantTLR | null>();
	@Input() set openRequests(obs: Observable<IntervenantTLR | null> | undefined) {
		this.openRequest$.registerSource(obs);
	}

	// Evenements à emettre
	@Output() intervenantCreated = new EventEmitter<void>();
	@Output() intervenantUpdated = new EventEmitter<void>();
	
	/////////////////////////
	// Actions utilisateur //
	/////////////////////////
	
	onSuggestionNomSelected(suggestion: SuggestionNomIntervenant | undefined) {
		if (!suggestion) {
			return;
		}
		
		let intervenantId = suggestion.id;
		this.intervenantService.getIntervenantTLRById(intervenantId).subscribe(
			(intervenant) => {
				this.switchToUpdateMode(intervenant);
			}
		)
	}
	
	private switchToUpdateMode(intervenant: IntervenantTLR) {
		this.popupVisibility$.next(false);

		// RDG: quand on switche sur une modification, on doit activer l'intervenant s'il ne l'est pas deja
		// Pour cela on va recopier l'intervenant, mais en changeant son status, avant d'afficher la vue
		let activeIntervenant = { ...intervenant } as IntervenantTLR;
		activeIntervenant.obsolete = false;
		
		this.openRequest$.next(activeIntervenant);
	}
	
	save() {
		let intervenant = this.intervenant;
		intervenant.nom = this.trim(intervenant.nom, '');
		intervenant.prenom = this.trim(intervenant.prenom, null);
		intervenant.email = this.trim(intervenant.email, null);
		intervenant.indicatifTelephone = this.trim(intervenant.indicatifTelephone, null);
		intervenant.telephone = this.trim(intervenant.telephone, null);

		let errors: string[] = [];

		if (intervenant.nom.length === 0) {
			errors.push("Le nom est obligatoire");
		} else if (intervenant.nom.length < 2) {
			// Ajout ici d'une regle de securite: le serveur va fabriquer le trigramme
			// utilisateur a partir de la premiere lettre du prenom et des deux premieres
			// du nom.
			// Si le nom a moins de deux lettres, cela provoque donc une erreur 
			errors.push("Le nom doit etre composé de deux lettres au minimum.");
		}

		if (intervenant.prenom === null) {
			errors.push("Le prénom est obligatoire");
		}
		
		// Evol 58826 - Si le telephone est rempli, l'indicatif doit etre fourni
		if (intervenant.telephone != null && intervenant.indicatifTelephone === null) {
			errors.push("L'indicatif téléphonique est obligatoire si un numéro de téléphone est fourni.");
		}
		
		// Evol 57782 - RG 3: Pour les internes, l'email doit respecter un format special
		if (intervenant.interne) {
			if (intervenant.email == null || intervenant.email.length === 0) {
				errors.push("L'email est obligatoire pour les internes");
			} else if (!this.internEmailPattern.test(intervenant.email)) {
				errors.push("Les internes doivent avoir un email au format XXXXXX@engie.com");
			}
		}
		
		if (this.isEmpty(intervenant.entrepriseId)) {
			if (this.isEmpty(this.obsoleteCurrentEntreprise)) {
				errors.push("L'entreprise est obligatoire");
			} else {
				intervenant.entrepriseId = (this.obsoleteCurrentEntreprise as EntrepriseTLR).id;
			}
		}

		if (this.isEmpty(intervenant.interne)) {
			errors.push('La valeur "Interne/Externe" doit être remplie.');
		}

		if (this.isEmpty(intervenant.obsolete)) {
			errors.push('La valeur "Actif" doit être remplie.');
		}
		
		if (this.isEmpty(intervenant.travailleurDetache)) {
			errors.push('La valeur "Travailleur Détaché" doit être remplie.');
		}
		
		if (errors.length > 0) {
			this.dialog.open(MessageDialogComponent, {
				width: '400px',
				data: {
					title: 'Enregistrement impossible',
					messages: errors
				}
			});

			return;
		}
		
		this.waitingForServer = true;
		if (this.mode === Mode.CREATION) {
			this.intervenantService.create(this.intervenant).subscribe(
				() => {
					this.waitingForServer = false;
					this.popupVisibility$.next(false);
					this.intervenantCreated.emit();
				},
				(response: HttpErrorResponse) => {
					this.waitingForServer = false;
					let message = "Le serveur a rencontré une erreur imprévue";
					if (response.error && response.error.message) {
						message = response.error.message;
					}					
					this.dialog.open(MessageDialogComponent, {
						width: '400px',
						data: {
							title: "Erreur lors de la création de l'intervenant",
							messages: [message]
						}
					});
					console.error("Erreur lors la tentivate de création de l'intervenant", response);
				}
			);
		} else {
			this.intervenantService.update(this.intervenant).subscribe(
				() => {
					this.waitingForServer = false;
					this.popupVisibility$.next(false);
					this.intervenantUpdated.emit();
				},
				(response: HttpErrorResponse) => {
					this.waitingForServer = false;
					let message = "Le serveur a rencontré une erreur imprévue";
					if (response.error && response.error.message) {
						message = response.error.message;
					}					
					this.dialog.open(MessageDialogComponent, {
						width: '400px',
						data: {
							title: "Erreur lors de la mise à jour de l'intervenant",
							messages: [message]
						}
					});
					console.error("Erreur lors la tentivate de la mise à jour de l'intervenant", response);
				}
			);
		}
	}
	
	private trim<T>(value: string | null | undefined, emptyValue: T): string | T {
		if (value === null || value === undefined) {
			return emptyValue;
		} else {
			let trimed = value.trim();
			return trimed.length === 0 ? emptyValue : trimed;
		}
	}
	
	private isEmpty(value: any): boolean {
		return value === null || value === undefined;
	}
}